import { LoadingService } from 'src/app/services/loading.service';
import { Component, Input, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartConfiguration } from 'chart.js';
import { Test } from 'src/app/models/test.model';
import { TestService } from 'src/app/services/test.service';
import { UtilsService } from 'src/app/services/utils.service';
import * as html2pdf from 'html2pdf.js';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'ct-language-report',
  templateUrl: './language-report.component.html',
  styleUrls: ['./language-report.component.scss']
})
export class LanguageReportComponent implements OnInit {

  @Input() test: Test;

  public currentDate = new Date();
  public data: number[];
  public times: string[];
  public reportType: 'candidate' | 'consultant' | 'client';
  public radarChartLabels: string[];
  public radarChartData: ChartConfiguration<'radar'>['data'];
  public radarChartOptions: ChartConfiguration<'radar'>['options'];
  public language: string;
  private languageId: number;
  public areaResultExplanation: string;


  private computedBodyStyle = getComputedStyle(document.body)

  public colors = {
    primaryColor: this.computedBodyStyle.getPropertyValue('--color-primary'),
    secondaryColor: this.computedBodyStyle.getPropertyValue('--color-secondary'),
    tertiaryColor: this.computedBodyStyle.getPropertyValue('--color-tertiary'),
    quaternaryColor: this.computedBodyStyle.getPropertyValue('--color-quaternary'),
    quinaryColor: this.computedBodyStyle.getPropertyValue('--color-quinary'),
    senaryColor: this.computedBodyStyle.getPropertyValue('--color-senary'),
    septenaryColor: this.computedBodyStyle.getPropertyValue('--color-septenary'),
    octonaryColor: this.computedBodyStyle.getPropertyValue('--color-octonary'),
    nonaryColor: this.computedBodyStyle.getPropertyValue('--color-nonary')
  }

  constructor(
    public renderer: Renderer2,
    private utilsService: UtilsService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private loadingService: LoadingService
  ) { }

  async ngOnInit() {
    this.activatedRoute.snapshot.queryParamMap.get('languageId')
      ? this.languageId = Number(this.activatedRoute.snapshot.queryParamMap.get('languageId'))
      : this.languageId = Number(this.test.reportLanguageId);
    this.language = this.utilsService.getLanguageFromLanguageId(this.languageId);
    this.translateService.use(this.language);
    this.translateService.setDefaultLang(this.language)
    document.documentElement.lang = this.language.split('-')[0];

    // En el caso de tenerlo seteamos los valores del proyecto
    if (this.test.projectSelected && this.test.projectSelected.projectId) {

      this.colors = {
        primaryColor: this.test.projectSelected.projectPrimaryColor,
        secondaryColor: this.test.projectSelected.projectSecondaryColor,
        tertiaryColor: this.test.projectSelected.projectTertiaryColor,
        quaternaryColor: this.test.projectSelected.projectQuaternaryColor,
        quinaryColor: this.test.projectSelected.projectQuinaryColor,
        senaryColor: this.test.projectSelected.projectSenaryColor,
        septenaryColor: this.test.projectSelected.projectSeptenaryColor,
        octonaryColor: this.test.projectSelected.projectOctonaryColor,
        nonaryColor: this.test.projectSelected.projectNonaryColor
      }
      document.getElementById('reportModule').style.setProperty('--color-primary', this.colors.primaryColor);
      document.getElementById('reportModule').style.setProperty('--color-secondary', this.colors.secondaryColor);
      document.getElementById('reportModule').style.setProperty('--color-tertiary', this.colors.tertiaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quaternary', this.colors.quaternaryColor);
      document.getElementById('reportModule').style.setProperty('--color-quinary', this.colors.quinaryColor);
      document.getElementById('reportModule').style.setProperty('--color-senary', this.colors.senaryColor);
      document.getElementById('reportModule').style.setProperty('--color-septenary', this.colors.septenaryColor);
      document.getElementById('reportModule').style.setProperty('--color-octonary', this.colors.octonaryColor);
      document.getElementById('reportModule').style.setProperty('--color-nonary', this.colors.nonaryColor);
    }

    switch (this.test.reportTypeId) {
      case '1': this.reportType = 'candidate'; break;
      case '2': this.reportType = 'consultant'; break;
      case '3': this.reportType = 'client'; break;
    }

    this.data = this.test.areas.map(area => area.score);

    // Espera para obtener las traducciones y evitar fallos en los instant
    await this.translateService.get('REPORT.CT.COMMON.PERQUESTION').toPromise();

    this.times = this.test.areas.map(area =>
      this.utilsService.secondsToTime(area.time / area.answered) + '/' + this.translateService.instant('REPORT.CT.COMMON.PERQUESTION')
    );

    if (this.test.result.completionPercentage) {
      this.radarChartLabels = Object.values(
        this.translateService.instant(this.test.areas.map(area => 'REPORT.CT.COMMON.AREA.' + area.description.toUpperCase()))
      ) as [];

      this.radarChartData = {
        labels: this.radarChartLabels,
        datasets: [{
          data: this.data,
          backgroundColor: this.colors.tertiaryColor,
          borderColor: this.colors.tertiaryColor,
          pointBackgroundColor: this.colors.primaryColor
        }]
      };

      this.radarChartOptions = {
        maintainAspectRatio: false,
        scales: {
          r: {
            pointLabels: {
              callback: (pointLabel) => [
                pointLabel,
                this.data[this.radarChartLabels.findIndex(x => x === pointLabel)] + '% ' +
                (this.reportType !== 'candidate' ? this.times[this.radarChartLabels.findIndex(x => x === pointLabel)] : '')
              ],
              color: this.colors.primaryColor,
              font: {
                size: 12
              }
            },
            angleLines: { color: this.colors.tertiaryColor, lineWidth: 1 },
            grid: { color: this.colors.tertiaryColor, lineWidth: 1 },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              display: false,
              maxTicksLimit: 1,
              stepSize: 10,
            }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false }
        }
      };
    }

    if (this.test.result.completionPercentage) {
      const areaScore1 = [];
      const areaScore2 = [];
      const areaScore3 = [];
      const areaScore4 = [];

      for (const area of this.test.areas) {
        if (area.score >= 70) {
          areaScore4.push(area);
        } else if (area.score >= 50) {
          areaScore3.push(area);
        } else if (area.score >= 30) {
          areaScore2.push(area);
        } else if (area.score < 30) {
          areaScore1.push(area);
        }
      }

      this.areaResultExplanation = '';

      if (areaScore1.length) {
        this.areaResultExplanation +=
          this.translateService.instant('REPORT.CT.' + this.test.testModel.id + '.' + this.reportType.toUpperCase() + '.AREARESULTEXPLANATION.SCORE_1')
          + areaScore1.map(area => this.translateService.instant('REPORT.CT.COMMON.AREA.MEANING.' + area.questionGroupId)).join(', ')
          + '. <br> <br>';
        this.areaResultExplanation = this.replaceSymbolByLetter(this.areaResultExplanation);
      }
      if (areaScore2.length) {
        this.areaResultExplanation +=
          this.translateService.instant('REPORT.CT.' + this.test.testModel.id + '.' + this.reportType.toUpperCase() + '.AREARESULTEXPLANATION.SCORE_2')
          + areaScore2.map(area => this.translateService.instant('REPORT.CT.COMMON.AREA.MEANING.' + area.questionGroupId)).join(', ')
          + '. <br> <br>';
        this.areaResultExplanation = this.replaceSymbolByLetter(this.areaResultExplanation);
      }
      if (areaScore3.length) {
        this.areaResultExplanation
          += this.translateService.instant('REPORT.CT.' + this.test.testModel.id + '.' + this.reportType.toUpperCase() + '.AREARESULTEXPLANATION.SCORE_3')
          + areaScore3.map(area => this.translateService.instant('REPORT.CT.COMMON.AREA.MEANING.' + area.questionGroupId)).join(', ')
          + '. <br> <br>';
        this.areaResultExplanation = this.replaceSymbolByLetter(this.areaResultExplanation);
      }
      if (areaScore4.length) {
        this.areaResultExplanation +=
          this.translateService.instant('REPORT.CT.' + this.test.testModel.id + '.' + this.reportType.toUpperCase() + '.AREARESULTEXPLANATION.SCORE_4')
          + areaScore4.map(area => this.translateService.instant('REPORT.CT.COMMON.AREA.MEANING.' + area.questionGroupId)).join(', ')
          + '. <br> <br>';
        this.areaResultExplanation = this.replaceSymbolByLetter(this.areaResultExplanation);
      }
    }
  }

  maxLevelApproved() {
    const index = this.test.levels.slice(-1)[0].levelDetail.cutOffMark < this.test.levels.slice(-1)[0].score ? -1 : -2;
    return this.test.levels.slice(index)[0].levelDetail.description;
  }

  print() {
    this.loadingService.startLoading();

    window.scrollTo(0, 0);
    this.renderer.setStyle(document.querySelector('body'), 'overflow', 'hidden');
    document.querySelectorAll('#languageReport').forEach(element => this.renderer.setAttribute(element, 'class', 'print'));
    const fileName = this.utilsService.reportFileName(this.test, '', this.reportType);


    return new Promise<void>(resolve => setTimeout(() => {
      let worker = html2pdf().from(document.querySelectorAll('#languageReport')[0]).set({
        pagebreak: { before: '.page, .break', avoid: 'p' },
        html2canvas: { scale: 2 },
        image: { type: 'jpeg', quality: 1 },
        filename: fileName
      }).toPdf();

      const pages = [...Array.from(document.querySelectorAll('#languageReport'))];
      for (const [i, page] of pages.slice(1).entries()) {
        worker = worker.get('pdf').then(pdf => {
          pdf.addPage();
        }).from(page).toContainer().toCanvas().toPdf();
      }

      worker = worker.get('pdf').then(pdf => this.addHeaderAndFooter(pdf)).save().then(() => {
        this.renderer.setStyle(document.querySelector('body'), 'overflow', 'initial');
        document.querySelectorAll('#languageReport').forEach(element => this.renderer.removeAttribute(element, 'class'));
        resolve();
      }).get('pdf').then(pdf => {
        // Escribir el base64 del pdf en el html para obtenerlo desde bowserprint
        document.querySelector('#base64').textContent = pdf.output('datauristring').toString().split('base64,')[1];
        document.querySelector('#base64').classList.add('base64');
      });

      setTimeout(() => {
        this.loadingService.stopLoading();
      }, 1000);
    }, 500));
  }

  // Req. 228077 (R) CT Test-Ingles: Modificar frase
  replaceSymbolByLetter(text: string) {
    const and = this.translateService.instant('COMMON.AND');

    // Contemplamos que sea más de un área
    if (text.split(', ').length !== 1) {
      const lastIndex = text.lastIndexOf(', ');
      text = text.substring(0, lastIndex) + and + text.substring(lastIndex + 1);
    }
    return text;
  }

  private addHeaderAndFooter(pdf) {
    const totalPages = pdf.internal.getNumberOfPages();

    for (let i = 2; i <= totalPages - 1; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(this.computedBodyStyle.getPropertyValue('--color-tertiary').trim());

      const relevateImg = new Image();
      relevateImg.src = 'assets/images/relevate-talent-assessment-logo.png';
      pdf.addImage(relevateImg, 'png', 15, 10, 55, 18);

      const randstadImg = new Image();
      randstadImg.src = 'assets/images/randstad-icon-pdf.png';
      pdf.addImage(randstadImg, 'png', 15, pdf.internal.pageSize.getHeight() - 19, 8, 4);

      pdf.text(
        (this.test.candidate.name
          ? this.utilsService.initials(this.test.candidate)
          : this.test.candidate.randstadCandidate) + ' | ' + (this.test.invitationId != 0 ? this.test.invitationId : this.test.externalId) + ' | | I/01/SP', 25, pdf.internal.pageSize.getHeight() - 15);

      pdf.text((i).toString(), pdf.internal.pageSize.getWidth() - 15, pdf.internal.pageSize.getHeight() - 15);
    }
  }

}
